import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Trash2, X, Bell } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function Notifications() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newNotification, setNewNotification] = useState({ title: "", description: "", time: "" });
  const [loading, setLoading] = useState(true);

  const fetchNotifications = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/notifications`)
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson && resJson.success) {
          setNotifications(resJson.notifications);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Notifications fetch error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSaveNotification = () => {
    if (!newNotification.title.trim() || !newNotification.description.trim()) {
      Swal.fire("Error", "Please provide a title and description!", "error");
      return;
    }

    Swal.fire({
      title: "Creating...",
      text: "Please wait.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    fetch(`${API_BASE_URL}/api/notifications`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newNotification)
    })
      .then(res => res.json())
      .then(resJson => {
        Swal.close();
        if (resJson && resJson.success) {
          Swal.fire("Success", "Notification created successfully!", "success");
          fetchNotifications();
          setNewNotification({ title: "", description: "", time: "" });
          setIsCreateModalOpen(false);
        } else {
          throw new Error(resJson.message || "Failed to save notification");
        }
      })
      .catch(err => {
        Swal.close();
        console.error("Save notification error:", err);
        Swal.fire("Error", err.message, "error");
      });
  };

  const handleDeleteNotification = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        fetch(`${API_BASE_URL}/api/notifications/${id}`, { method: "DELETE" })
          .then(res => res.json())
          .then(resJson => {
            Swal.close();
            if (resJson && resJson.success) {
              Swal.fire('Deleted!', 'Notification has been deleted.', 'success');
              fetchNotifications();
            } else {
              throw new Error(resJson.message || "Failed to delete notification");
            }
          })
          .catch(err => {
            Swal.close();
            console.error("Delete error:", err);
            Swal.fire("Error", err.message, "error");
          });
      }
    });
  };

  return (
    <div className="flex bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Notifications Management"
          subtitle="View and manage all system and event notifications."
        />

        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full">
          {/* Header Section */}
          <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-center justify-end gap-4">

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <button
                onClick={() => { setNewNotification({ title: "", description: "", time: "" }); setIsCreateModalOpen(true); }}
                className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
                <Plus size={16} />
                Add Notification
              </button>
            </div>
          </div>

          {/* Table Card */}
          <div className="bg-white dark:bg-slate-800 shadow-sm rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1">
            {/* Toolbar */}
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm placeholder-slate-400"
                />
              </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                <thead className="bg-slate-50/50 dark:bg-slate-800/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700/50">
                  <tr>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-900/50 z-20">Title</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Description</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Timestamp</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100/50 dark:divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-slate-500 dark:text-slate-400 animate-pulse">
                        Loading notifications...
                      </td>
                    </tr>
                  ) : notifications.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-slate-500 dark:text-slate-400">
                        No notifications configured yet.
                      </td>
                    </tr>
                  ) : (
                    notifications.filter(n => n.title.toLowerCase().includes(searchTerm.toLowerCase())).map((notif) => (
                      <tr key={notif.notification_id_PK} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/80 transition-colors group">
                        <td className="p-4 sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-100 dark:bg-blue-900/30">
                              <Bell className="text-blue-500" size={20} />
                            </div>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{notif.title}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600 dark:text-slate-400">
                          <div className="max-w-[400px] truncate" title={notif.description}>
                            {notif.description}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600 dark:text-slate-400">
                          {notif.time ? notif.time : new Date(notif.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleDeleteNotification(notif.notification_id_PK)}
                            className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                            title="Delete Notification"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                  {!loading && notifications.filter(n => n.title.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && notifications.length > 0 && (
                    <tr>
                      <td colSpan="4" className="p-8 text-center text-slate-500 dark:text-slate-400">
                        No notifications found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md relative z-10 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold dark:text-white">Create Notification</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 dark:text-slate-300">Title</label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={e => setNewNotification({ ...newNotification, title: e.target.value })}
                  placeholder="e.g. System Maintenance"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 dark:text-slate-300">Description</label>
                <textarea
                  rows="4"
                  value={newNotification.description}
                  onChange={e => setNewNotification({ ...newNotification, description: e.target.value })}
                  placeholder="Describe the notification message..."
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 dark:text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 dark:text-slate-300">Time / Date</label>
                <input
                  type="text"
                  value={newNotification.time}
                  onChange={e => setNewNotification({ ...newNotification, time: e.target.value })}
                  placeholder="e.g. 20 March 2026, 8:00 AM"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl text-sm font-medium transition-colors">Cancel</button>
              <button onClick={handleSaveNotification} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">Save Notification</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
